// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Modal, Form, Col, Row } from "react-bootstrap";
import InputNumber from "rc-input-number";

import { ModalPanel } from "states/guistates";
import { ActionType, IAction, IActionTypeChangeTrendMonitorSettings, raiseAction } from "actions";
import { IState } from "states/states";
import { DataState } from "types";
import { Period, TrendMonitor } from "shared/types/cloudtypes";
import { getEnergyUnit } from "guiutils";
import { ITariffCircuit } from "shared/types/parameter";
import { isSet } from "shared/utils/utils";

import SaveFeedback from "view/comp/SaveFeedback";

type IModalTrendMonitorSettingsItemProps = {
  siteId: number | undefined;
  trendmonitorId: number | undefined; // undefined for new trendmonitors
  isOpen: boolean;
  dataState: DataState;
  oldValue: TrendMonitor | undefined;
  tariffCircuits: ITariffCircuit[];
};

const mapStateToProps = (state: IState) => {
  const isOpen = state.gui.modal.panel === ModalPanel.TRENDMONITOR_SETTINGS;
  const siteId = state.cached.selectedSite;
  const site = state.site.siteList?.find((curSite) => curSite.id === siteId);
  if (site === undefined) {
    console.error("admin unidentified trendmonitor site", siteId, state.site.siteList);
  }

  const oldValue = site?.cloudSettings?.trendMonitor?.[state.gui.modalTrendMonitor!];
  const props: IModalTrendMonitorSettingsItemProps = {
    isOpen,
    siteId,
    trendmonitorId: state.gui.modalTrendMonitor,
    dataState: state.scheduler.saveState,
    oldValue,
    tariffCircuits: state.para.parameter?.tariffline ?? [],
  };
  return props;
};

type IModalTrendMonitorSettingsState = TrendMonitor & {
  wasOpen: boolean;
  isChanged: boolean;
};

interface IModalTrendMonitorSettingsItemDispatch {
  onCancelClick: () => void;
  onSaveClick: (
    siteId: number,
    trendMonitorId: number | undefined,
    state: IModalTrendMonitorSettingsState,
  ) => void;
  onDeleteClick: (siteId: number, trendMonitorId: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IModalTrendMonitorSettingsItemDispatch = {
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
    },
    onSaveClick: (
      siteId: number,
      trendMonitorId: number | undefined,
      state: IModalTrendMonitorSettingsState,
    ) => {
      const { wasOpen, isChanged, ...settings } = state;
      const payload: IActionTypeChangeTrendMonitorSettings = {
        siteId,
        trendMonitorId,
        trendMonitorSettings: settings,
      };
      dispatch(raiseAction(ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_REQ, payload));
    },
    onDeleteClick: (siteId: number, trendMonitorId: number) => {
      const payload: IActionTypeChangeTrendMonitorSettings = {
        siteId,
        trendMonitorId,
        trendMonitorSettings: undefined,
      };
      dispatch(raiseAction(ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_REQ, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  titleDefault: {
    id: "new trend monitor",
  },
  titleHint: {
    id: "custom name of trend monitor",
  },
  toleranceHint: {
    id: "the tolerance for the target value overshoot alarm",
  },
  slopeHint: {
    id: "the tolerance slope for the target value overshoot during the period",
  },
});

class ModalTrendMonitorSettingsItemComponent extends React.Component<
  IModalTrendMonitorSettingsItemProps & IModalTrendMonitorSettingsItemDispatch,
  IModalTrendMonitorSettingsState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  state: IModalTrendMonitorSettingsState = {
    wasOpen: false,
    isChanged: false,
    ...this.makeEmptyTrendMonitor(),
  };

  componentDidUpdate() {
    // check for changes in old value
    if (this.props.isOpen !== this.state.wasOpen) {
      if (isSet(this.props.trendmonitorId)) {
        this.setState({
          wasOpen: this.props.isOpen,
          isChanged: false,
          ...this.props.oldValue!,
        });
      } else {
        this.setState({
          wasOpen: this.props.isOpen,
          isChanged: false,
          ...this.makeEmptyTrendMonitor(),
        });
      }
    }
  }

  public render() {
    if (!this.props.isOpen) return <></>; // avoid rendering of inexisting data

    return (
      <Modal show={this.props.isOpen} onHide={this.props.onCancelClick}>
        <Modal.Header closeButton>
          <FormattedMessage id="Trend Monitor Settings" />
        </Modal.Header>
        <Modal.Body>{this.renderSettings()}</Modal.Body>
        <Modal.Footer>
          <SaveFeedback dataState={this.props.dataState} />
          <Button
            id="cancelTrendMonitorSettings"
            variant="secondary"
            className="me-1"
            onClick={this.props.onCancelClick}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          {(this.state.isChanged || this.props.trendmonitorId === undefined) && (
            <Button
              id="saveTrendMonitorSettings"
              variant="primary"
              className="me-1"
              onClick={(e) =>
                this.props.onSaveClick(this.props.siteId!, this.props.trendmonitorId, this.state)
              }
            >
              <FormattedMessage id="Save" />
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    );
  }

  private renderSettings() {
    return (
      <>
        <h1>
          <FormattedMessage id="Trend Monitor Settings" />
        </h1>
        <Form.Group as={Row} className="mb-3">
          <Form.Label htmlFor="title" sm={3}>
            <FormattedMessage id="Title" />
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              id="title"
              placeholder={this.context.formatMessage(messages.titleHint)}
              type="text"
              value={this.state.title}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  title: event?.currentTarget.value,
                });
              }}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Col sm={9} className="mt-1">
            <Form.Check
              id="isActive"
              className="mt-2"
              checked={this.state.isActive}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  isActive: event?.currentTarget.checked,
                });
              }}
              label={<FormattedMessage id="Activated" />}
            />
            {!this.state.isActive && isSet(this.props.trendmonitorId) && (
              <Button
                style={{ marginTop: -2 }}
                id="deleteTrendMonitor"
                variant="danger"
                className="ms-3"
                onClick={(_event) =>
                  this.props.onDeleteClick(this.props.siteId!, this.props.trendmonitorId!)
                }
              >
                <FormattedMessage id="Delete Trend Monitor" />
              </Button>
            )}
          </Col>
        </Form.Group>
        {this.renderCircuit()}
        {this.renderPeriod()}
        {this.renderTarget()}
        {this.renderAlarmSettings()}
      </>
    );
  }

  private renderCircuit() {
    const tariffIds = Array.from(Array(this.props.tariffCircuits.length).keys())
      .filter((curId) => this.props.tariffCircuits[curId].webActive)
      .sort((a, b) =>
        this.props.tariffCircuits[a].name.localeCompare(this.props.tariffCircuits[b].name),
      );
    const tariffItems = tariffIds.map((curIndex) => {
      return (
        <option key={`${curIndex}`} value={this.props.tariffCircuits[curIndex].id ?? curIndex}>
          {this.props.tariffCircuits[curIndex].name}
        </option>
      );
    });

    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label sm={3}>
          <FormattedMessage id="Tariff Circuit" />
        </Form.Label>
        <Col sm={9}>
          <>
            <Form.Select
              className="mb-2"
              value={this.state.circuit}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  circuit: parseInt(event.currentTarget.value),
                });
              }}
            >
              {tariffItems}
            </Form.Select>
          </>
        </Col>
      </Form.Group>
    );
  }

  private renderPeriod() {
    const periodItems = [Period.DAY, Period.MONTH, Period.YEAR].map((curItem) => {
      return (
        <option key={`${curItem}`} value={`${curItem}`}>
          {this.context.formatMessage({ id: `Period_${curItem}` })}
        </option>
      );
    });

    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label htmlFor="period" sm={3}>
          <FormattedMessage id="Period" />
        </Form.Label>
        <Col sm={9}>
          <Form.Select
            id="period"
            value={this.state.period}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                period: event.currentTarget.value as Period,
              });
            }}
          >
            {periodItems}
          </Form.Select>
        </Col>
      </Form.Group>
    );
  }

  private renderTarget() {
    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label sm={3}>
          <FormattedMessage id="Reference" />
        </Form.Label>
        <Col sm={9} className="mt-2">
          <Form.Check
            id="reference_value"
            name="reference"
            type="radio"
            className="mb-2"
            checked={!isSet(this.state.referencePeriod)}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                referencePeriod: undefined,
              });
            }}
            label={this.renderReferenceValueLabel()}
          />
          <Form.Check
            id="reference_date"
            name="reference"
            type="radio"
            disabled={true}
            checked={isSet(this.state.referencePeriod)}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                referencePeriod: Period.YEAR,
              });
            }}
            label={this.renderReferenceDateLabel()}
          />
        </Col>
      </Form.Group>
    );
  }

  private renderReferenceValueLabel() {
    return (
      <>
        {" "}
        <FormattedMessage id="value" />
        {!isSet(this.state.referencePeriod) && (
          <>
            :{" "}
            <InputNumber
              type="number"
              value={this.state.targetValue ?? 0}
              min={0}
              onChange={(number) => {
                this.setState({
                  isChanged: true,
                  targetValue: number ? number : undefined,
                });
              }}
            />{" "}
            {getEnergyUnit(this.props.tariffCircuits[this.state.circuit]?.unit, false)}
          </>
        )}
      </>
    );
  }

  private renderReferenceDateLabel() {
    const periodRefItems = [Period.YEAR, Period.MONTH, Period.DAY].map((curItem) => {
      return (
        <option key={`${curItem}`} value={`${curItem}`}>
          {this.context.formatMessage({ id: `Period_${curItem}` })}
        </option>
      );
    });
    return (
      <>
        <FormattedMessage id="previous time period" />
        {isSet(this.state.referencePeriod) && (
          <Form.Select
            value={this.state.referencePeriod}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                referencePeriod: event.currentTarget.value as Period,
              });
            }}
          >
            {periodRefItems}
          </Form.Select>
        )}
      </>
    );
  }

  private renderAlarmSettings() {
    return (
      <>
        <Form.Group as={Row} className="mb-3">
          <Form.Label htmlFor="tolerance" sm={3}>
            <FormattedMessage id="Alarm Tolerance" />
          </Form.Label>
          <Col sm={9} className="mt-2">
            <InputNumber
              id="tolerance"
              title={this.context.formatMessage(messages.toleranceHint)}
              type="number"
              min={0}
              step={0.05}
              value={this.state.tolerance}
              onChange={(number) => {
                this.setState({
                  isChanged: true,
                  tolerance: number ?? 0,
                });
              }}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label htmlFor="slope" sm={3}>
            <FormattedMessage id="Alarm Slope" />
          </Form.Label>
          <Col sm={9} className="mt-2">
            <InputNumber
              id="slope"
              title={this.context.formatMessage(messages.slopeHint)}
              type="number"
              step={0.05}
              value={this.state.slope}
              onChange={(number) => {
                this.setState({
                  isChanged: true,
                  slope: number ?? 0,
                });
              }}
            />
          </Col>
        </Form.Group>
      </>
    );
  }

  private makeEmptyTrendMonitor() {
    return {
      title: this.context.formatMessage(messages.titleDefault),
      isActive: true,
      circuit: 0,
      period: Period.MONTH,
      targetValue: 100,
      tolerance: 0.05,
      slope: 0.95,
    } as TrendMonitor;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ModalTrendMonitorSettingsItemComponent);
